import { awaitRedraw, el, KEYED_LIST, resizer } from "@dhx/ts-common/dom";
import { getScrollbarWidth, eventHandler, locate } from "@dhx/ts-common/html";
import { calculatePositions } from "../helpers/data";
import { isAutoWidth, isCssSupport, getTotalWidth } from "../helpers/main";
import { ICoords, IGrid, IRendererConfig, IGridConfig, IRow, IProGrid } from "../types";
import { getCells, getHandlers, getShifts, getSpans } from "./Cells";
import { getFixedCols, getFixedColsHeader } from "./FixedCols";
import { getFixedRows } from "./FixedRows";
import { findIndex } from "@dhx/ts-common/core";

const BORDERS = 2;

export function getRenderConfig(obj: any, data: IRow[], wrapperSizes: any): IRendererConfig {
	const config = obj.config;
	const columns = config.columns.filter(col => !col.hidden);
	const positions = calculatePositions(wrapperSizes.width, wrapperSizes.height, obj._scroll, config, data);
	const currentColumns = columns.slice(positions.xStart, positions.xEnd);
	const currentRows = data.slice(positions.yStart, positions.yEnd);

	return {
		...config,
		data,
		columns,
		scroll: obj._scroll,
		$positions: positions,
		headerHeight: config.$headerLevel * config.headerRowHeight,
		footerHeight: config.$footerLevel * config.footerRowHeight,
		firstColId: columns[0] && columns[0].id,
		events: obj.events,
		_events: obj._events,
		currentColumns,
		currentRows,
		sortBy: obj._sortBy,
		sortDir: obj._sortDir,
		content: obj.content,
		gridId: obj._uid,
	};
}

function getElementSizes(element: HTMLElement | any): { width: number; height: number } {
	if (!element) return;
	if (!(element as HTMLElement).tagName) element = element._parent._container;
	if (!element) return;
	const styles = element.currentStyle || window.getComputedStyle(element);
	const paddingsByWidth = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight) || 0;
	const paddingsByHeight = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom) || 0;
	return {
		width: element.clientWidth - paddingsByWidth,
		height: element.clientHeight - paddingsByHeight,
	};
}

function getGridData(renderConfig: IRendererConfig, shifts: ICoords): any {
	const content = getCells(renderConfig);
	const contentSpans = getSpans(renderConfig);
	const getRowAriaAttrs = count => ({
		role: "rowgroup",
		"aria-rowcount": count,
	});

	let resizedLine;
	const colums = renderConfig.columns;
	if (renderConfig.$resizing) {
		const colIndex = findIndex(colums, col => col.id === renderConfig.$resizing);
		const firstCellLeft = getTotalWidth(colums.slice(0, colIndex)) + colums[colIndex].$width;
		resizedLine = el(".dhx_grid-resize-line", {
			style: {
				top: 0,
				left: firstCellLeft,
				height: renderConfig.$totalHeight,
			},
		});
	}
	let selection = renderConfig.selection ? renderConfig.selection.toHTML() : null;
	selection =
		typeof selection === "string" ? el("div.dhx_selection", { ".innerHTML": selection }) : selection;

	const pos = renderConfig.$positions;
	const events = {};

	function getCellInfo(e) {
		const rowId = locate(e, "dhx_id");
		const colId = locate(e, "dhx_col_id");
		const row = renderConfig.currentRows.filter(item => item.id === rowId)[0];
		const col = renderConfig.currentColumns.filter(item => item.id === colId)[0];

		return {
			row: rowId ? row : {},
			col: colId ? col : {},
		};
	}

	if (renderConfig.eventHandlers) {
		for (const key in renderConfig.eventHandlers) {
			if (renderConfig.eventHandlers.hasOwnProperty(key)) {
				const event = renderConfig.eventHandlers[key];
				events[key] = eventHandler(e => getCellInfo(e), {
					...event,
				});
			}
		}
	}

	return el(
		".dhx_data-wrap",
		{
			style: {
				height: renderConfig.$totalHeight,
				width: renderConfig.$totalWidth,
				"padding-left": shifts.x,
				"padding-top": shifts.y,
			},
			role: "presentation",
			...events,
		},
		[
			el(
				".dhx_grid_data",
				{
					_flags: KEYED_LIST,
					...getHandlers(pos.yStart, pos.xStart, renderConfig),
					...getRowAriaAttrs(renderConfig.data.length),
				},
				content
			),
			el(".dhx_span-spans", { role: "presentation" }, contentSpans),
			el(".dhx_grid_selection", { _ref: "selection", "aria-hidden": "true" }, [selection, resizedLine]),
		]
	);
}

function getContentHeight(
	renderConfig: IRendererConfig,
	isSticky: boolean,
	wrapperSizes: { width: number; height: number }
): number {
	let contentHeight = wrapperSizes.height - BORDERS;
	contentHeight = isSticky ? contentHeight : contentHeight - renderConfig.headerHeight;

	const isFooter = renderConfig.$footer;

	return (contentHeight = isFooter
		? isSticky
			? contentHeight
			: contentHeight - renderConfig.footerHeight
		: contentHeight);
}

function applyAutoWidth(
	config: IGridConfig,
	wrapperSizes,
	firstApply = true,
	resizer = false,
	scrollViewConfig = false
) {
	const scrollbarY =
		!scrollViewConfig && config.$totalHeight >= wrapperSizes.height - config.headerRowHeight
			? getScrollbarWidth()
			: 0;
	const newTotalWidth = wrapperSizes.width - BORDERS - scrollbarY;

	const columns = config.columns.filter(col => !col.hidden);

	const growingColumns = columns.filter(
		col => !col.width && !(col as any).$fixed && isAutoWidth(config, col)
	);
	let nonGrowingColumnsWidth = getTotalWidth(
		columns.filter(col => col.width || (col as any).$fixed || !isAutoWidth(config, col))
	);

	const fullGravity = growingColumns.reduce((gravity, col) => gravity + (col.gravity || 1), 0);

	if (newTotalWidth < config.$totalWidth) {
		const growingColumnsWidth = growingColumns.reduce(
			(width, col) => width + (col.maxWidth || col.$width),
			0
		);
		if (growingColumns.length > 1) {
			growingColumns.forEach(col => {
				let newWidth = 0;
				if (firstApply) {
					newWidth =
						Math.abs(newTotalWidth - growingColumnsWidth) * ((col.gravity || 1) / fullGravity);
				} else {
					newWidth =
						Math.abs(newTotalWidth - nonGrowingColumnsWidth) * ((col.gravity || 1) / fullGravity);
				}
				const wrongMin = newWidth < col.minWidth;
				const wrongMax = newWidth > col.maxWidth;
				if (!wrongMin && !wrongMax) {
					col.$width = newWidth;
				} else if (wrongMin) {
					nonGrowingColumnsWidth += col.$width - newWidth;
					(col as any).$fixed = true;
				} else if (wrongMax) {
					col.$width = col.maxWidth;
					(col as any).$fixed = true;
				}
			});
		}
	} else {
		growingColumns.forEach(col => {
			const newWidth =
				Math.abs(newTotalWidth - nonGrowingColumnsWidth) * ((col.gravity || 1) / fullGravity);
			const wrongMin = newWidth < col.minWidth;
			const wrongMax = newWidth > col.maxWidth;
			if (!wrongMin && !wrongMax) {
				col.$width = newWidth;
			} else if (wrongMin) {
				nonGrowingColumnsWidth += col.$width - newWidth;
				if (resizer) (col as any).$fixed = true;
			} else if (wrongMax) {
				col.$width = col.maxWidth;
				if (resizer) (col as any).$fixed = true;
			}
			if (!firstApply && (col as any).$fixed) {
				delete (col as any).$fixed;
			}
		});
	}
	if (firstApply) {
		applyAutoWidth(config, wrapperSizes, false, resizer, scrollViewConfig);
	}
}

export function render(vm: any, obj: IGrid, htmlEvents: any, selection: any, uid: string) {
	if (!(obj as any)._container) {
		obj.config.width = 1;
		obj.config.height = 1;
	}
	// if grid placed inside another component, it will fit to its container
	if (vm && vm.node && vm.node.parent && vm.node.parent.el) {
		const parentNode = vm.node.parent.el;
		const parentSizes = getElementSizes(parentNode);
		obj.config.width = parentSizes.width;
		obj.config.height = parentSizes.height;
	}
	const config = obj.config;
	// when grid is destructing and user try to repaint it
	if (!config) {
		return el("div");
	}
	if (!config.columns.length) {
		return el(".dhx_grid", {
			dhx_root_id: config.rootParent,
		});
	}

	const data = obj.data.getRawData(0, -1, null, 2);

	if (config.columns.reduce((check, col) => (check = !col.hidden ? col.hidden : check), true)) {
		config.$totalHeight = 0;
	} else {
		config.$totalHeight = data.reduce((total, { $height }) => (total += $height || 0), 0);
	}

	const sizes = getElementSizes((obj as any)._container);
	const wrapperSizes = {
		width: (config.width ? config.width : sizes && sizes.width) || 0,
		height: (config.height ? config.height : sizes && sizes.height) || 0,
	};
	// TODO: Remove scroll
	if (isAutoWidth(config)) {
		applyAutoWidth(config, wrapperSizes);
		config.$totalWidth = getTotalWidth(config.columns.filter(col => !col.hidden));
	}
	config.width = wrapperSizes.width;
	config.height = wrapperSizes.height;

	const renderConfig = getRenderConfig(obj, data, wrapperSizes);
	renderConfig.selection = selection;
	renderConfig.datacollection = obj.data;
	const shifts = getShifts(renderConfig);

	const isSticky = isCssSupport("position", "sticky");
	const gridBodyHeight = getContentHeight(renderConfig, isSticky, wrapperSizes);

	const layoutState = {
		wrapper: wrapperSizes,
		sticky: isSticky,
		shifts,
		gridBodyHeight,
	};

	const header = getFixedRows(renderConfig, {
		...layoutState,
		name: "header",
		position: "top",
	});

	const footer = renderConfig.$footer
		? getFixedRows(renderConfig, {
				...layoutState,
				name: "footer",
				position: "bottom",
		  })
		: null;

	const lessByWidth = renderConfig.$totalWidth + BORDERS < wrapperSizes.width ? "dhx_grid-less-width" : "";
	const lessByHeight =
		renderConfig.$totalHeight + BORDERS < wrapperSizes.height ? "dhx_grid-less-height" : "";

	const getGridAriaAttrs = (rows, cols, isEditable, isMultiselectable) => ({
		role: "grid",
		"aria-rowcount": rows.length,
		"aria-colcount": cols.filter(col => !col.hidden).length,
		"aria-readonly": isEditable ? "false" : "true",
		"aria-multiselectable": isMultiselectable ? "true" : "false",
	});

	// dirty: but work. Change checking of rendering Grid
	if (!vm.node) {
		const { x, y } = obj.getScrollState();
		awaitRedraw().then(() => {
			obj.scroll(x, y);
		});
	}
	return el(
		".dhx_grid.dhx_widget",
		{
			class:
				(renderConfig.css || "") +
				(!isSticky ? " dhx_grid_border" : "") +
				(config.multiselection ? " dhx_no-select--pointer" : ""),
			dhx_widget_id: uid,
			dhx_root_id: config.rootParent,
			...getGridAriaAttrs(
				renderConfig.data,
				config.columns,
				renderConfig.editable,
				renderConfig.multiselection
			),
		},
		[
			resizer(changeWith => {
				if (isAutoWidth(obj.config) && !!changeWith) {
					config.$totalWidth = 0;
					applyAutoWidth(config, wrapperSizes, true, true);
				}
				return obj.paint();
			}),
			el(
				".dhx_grid-content",
				{
					style: { ...wrapperSizes },
					onclick: htmlEvents.onclick,
					onmouseover: htmlEvents.onmouseover,
					class: `${lessByWidth} ${lessByHeight}`.trim(),
					role: "presentation",
				},
				[
					isSticky ? null : header,
					el(
						".dhx_grid-body",
						{
							style: {
								height: gridBodyHeight,
								width: wrapperSizes.width - BORDERS,
							},
							onscroll: htmlEvents.onscroll,
							_ref: "grid_body",
							role: "presentation",
						},
						[
							el("div", {}, [
								isSticky ? header : null,
								getGridData(renderConfig, shifts),
								isSticky ? footer : null,
							]),
						]
					),
					getFixedColsHeader(renderConfig, layoutState),
					getFixedCols(renderConfig, layoutState),
					isSticky ? null : footer,
				]
			),
		]
	);
}

export function proRender(vm: any, obj: IProGrid, htmlEvents: any, selection: any, uid: string) {
	if (!(obj as any)._container) {
		obj.config.width = 1;
		obj.config.height = 1;
	}
	// if grid placed inside another component, it will fit to its container
	if (vm && vm.node && vm.node.parent && vm.node.parent.el) {
		const parentNode = vm.node.parent.el;
		const parentSizes = getElementSizes(parentNode);
		obj.config.width = parentSizes.width;
		obj.config.height = parentSizes.height;
	}
	const config = obj.config;
	// when grid is destructing and user try to repaint it
	if (!config) {
		return el("div");
	}
	if (!config.columns.length) {
		return el(".dhx_grid", {
			dhx_root_id: config.rootParent,
		});
	}

	const data = obj.data.getRawData(0, -1, null, 2);

	if (config.columns.reduce((check, col) => (check = !col.hidden ? col.hidden : check), true)) {
		config.$totalHeight = 0;
	} else {
		config.$totalHeight = data.reduce((total, { $height }) => (total += $height || 0), 0);
	}

	const sizes = getElementSizes((obj as any)._container);
	const wrapperSizes = {
		width: (config.width ? config.width : sizes && sizes.width) || 0,
		height: (config.height ? config.height : sizes && sizes.height) || 0,
	};
	// TODO: Remove scroll
	if (isAutoWidth(config)) {
		applyAutoWidth(
			config,
			wrapperSizes,
			true,
			false,
			(obj as any).scrollView && (obj as any).scrollView.config.enable
		);
		config.$totalWidth = getTotalWidth(config.columns.filter(col => !col.hidden));
	}
	config.width = wrapperSizes.width;
	config.height = wrapperSizes.height;

	const renderConfig = getRenderConfig(obj, data, wrapperSizes);
	renderConfig.selection = selection;
	renderConfig.datacollection = obj.data;
	const shifts = getShifts(renderConfig);

	const isSticky = isCssSupport("position", "sticky");
	const gridBodyHeight = getContentHeight(renderConfig, isSticky, wrapperSizes);

	const layoutState = {
		wrapper: wrapperSizes,
		sticky: isSticky,
		shifts,
		gridBodyHeight,
	};

	const header = getFixedRows(renderConfig, {
		...layoutState,
		name: "header",
		position: "top",
	});

	const footer = renderConfig.$footer
		? getFixedRows(renderConfig, {
				...layoutState,
				name: "footer",
				position: "bottom",
		  })
		: null;

	const lessByWidth = renderConfig.$totalWidth + BORDERS < wrapperSizes.width ? "dhx_grid-less-width" : "";
	const lessByHeight =
		renderConfig.$totalHeight + BORDERS < wrapperSizes.height ? "dhx_grid-less-height" : "";

	// dirty: but work. Change checking of rendering Grid
	if (!vm.node) {
		const { x, y } = obj.getScrollState();
		awaitRedraw().then(() => {
			obj.scroll(x, y);
		});
	}

	const gridContent = el("div", {}, [
		isSticky ? header : null,
		getGridData(renderConfig, shifts),
		isSticky ? footer : null,
	]);

	return el(
		".dhx_grid.dhx_widget",
		{
			class:
				(renderConfig.css || "") +
				(!isSticky ? " dhx_grid_border" : "") +
				(config.multiselection ? " dhx_no-select--pointer" : ""),
			dhx_widget_id: uid,
			dhx_root_id: config.rootParent,
			role: "grid", // TODO: better to show rowcount and colcount here, but how get these numbers here?
			"aria-rowcount": renderConfig.data.length,
			"aria-colcount": config.columns.filter(col => !col.hidden).length,
		},
		[
			resizer(changeWith => {
				if (isAutoWidth(obj.config) && !!changeWith) {
					config.$totalWidth = 0;
					applyAutoWidth(config, wrapperSizes, true, true);
				}
				return obj.paint();
			}),
			el(
				".dhx_grid-content",
				{
					style: { ...wrapperSizes },
					onclick: htmlEvents.onclick,
					onmouseover: htmlEvents.onmouseover,
					class: `${lessByWidth} ${lessByHeight}`.trim(),
					role: "presentation",
				},
				[
					isSticky ? null : header,
					el(
						".dhx_grid-body",
						{
							style: {
								height: gridBodyHeight,
								width: wrapperSizes.width - BORDERS,
							},
							onscroll: htmlEvents.onscroll,
							_ref: "grid_body",
							role: "presentation",
						},
						[
							(obj as any).scrollView && (obj as any).scrollView.config.enable
								? obj.scrollView.render([gridContent])
								: gridContent,
						]
					),
					getFixedColsHeader(renderConfig, layoutState),
					getFixedCols(renderConfig, layoutState),
					isSticky ? null : footer,
				]
			),
		]
	);
}
